import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";

export const URL_REPORTES = process.env.REACT_APP_URL_REPORTES;
export const fmtN = (v) => new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(v || 0);
export const fmtM = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(v || 0);
export const fmtP = (v) => `${new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 }).format(v || 0)}%`;
const fmtK = (v) => (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`);
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
export const nombreMes = (k) => { const [a, m] = k.split("-"); return `${MESES[Number(m) - 1]} ${a.slice(2)}`; };

// Paleta validada con validate_palette.js; el orden es fijo
export const COLORES = ["#2563eb", "#0d9488", "#d97706", "#7c3aed", "#db2777", "#65a30d"];
export const GRIS = "#cbd5e1";

export const PERIODOS = {
  mes: ["Este mes", () => [moment().startOf("month"), moment()]],
  tres: ["3 meses", () => [moment().subtract(2, "months").startOf("month"), moment()]],
  anio: ["Este año", () => [moment().startOf("year"), moment()]],
  doce: ["12 meses", () => [moment().subtract(11, "months").startOf("month"), moment()]],
};
const rango = (k) => PERIODOS[k][1]().map((d) => d.format("YYYY-MM-DD"));

export function usePeriodo(inicial = "doce") {
  const [activo, setActivo] = useState(inicial);
  const [[inicio, fin], setFechas] = useState(rango(inicial));
  return { activo, inicio, fin, elegir: (k) => { setActivo(k); setFechas(rango(k)); }, manual: (i, f) => { setActivo(""); setFechas([i, f]); } };
}

export function useDatos(ruta) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let vivo = true;
    setError("");
    axios.get(`${URL_REPORTES}${ruta}`, { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } })
      .then((r) => vivo && setDatos(r.data))
      .catch((e) => vivo && setError(e.response?.data?.message || "No se pudo cargar el reporte"));
    return () => { vivo = false; };
  }, [ruta]);
  return { datos, error };
}

export function Pagina({ titulo, subtitulo, periodo, acciones, error, cargando, children }) {
  const { user } = useContext(AuthContext);
  return (
    <>
      <Header />
      {user.menu_reportes ? (
        <div className="card container col-12 rp">
          <div className="page-header">
            <div><h3 className="page-title">{titulo}</h3><p className="page-subtitle">{subtitulo}</p></div>
            {acciones}
          </div>
          {periodo && (
            <div className="rp-filtros">
              {Object.entries(PERIODOS).map(([k, [t]]) => (
                <button key={k} type="button" className={periodo.activo === k ? "activo" : ""} onClick={() => periodo.elegir(k)}>{t}</button>
              ))}
              <input type="date" aria-label="Desde" value={periodo.inicio} onChange={(e) => periodo.manual(e.target.value, periodo.fin)} />
              <input type="date" aria-label="Hasta" value={periodo.fin} onChange={(e) => periodo.manual(periodo.inicio, e.target.value)} />
            </div>
          )}
          {error ? <p className="rp-vacio">{error}</p> : cargando ? <p className="rp-vacio">Cargando...</p> : children}
        </div>
      ) : null}
    </>
  );
}

export const Kpis = ({ items }) => (
  <div className="rp-kpis">
    {items.map((k) => (
      <div key={k.t} className="rp-kpi">
        <span>{k.t}</span><strong>{k.v}</strong>{k.d ? <small className={k.tono || ""}>{k.d}</small> : null}
      </div>
    ))}
  </div>
);

export const Tarjeta = ({ titulo, nota, ancho, children }) => (
  <section className={`rp-card ${ancho ? "ancho" : ""}`}>
    <header><h4>{titulo}</h4>{nota ? <small>{nota}</small> : null}</header>
    {children}
  </section>
);

const Vacio = () => <p className="rp-vacio">Sin datos en este periodo</p>;

export function Leyenda({ series }) {
  if (series.length < 2) return null;
  return <div className="rp-leyenda">{series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}</div>;
}

// Columnas por mes. Comparte un solo eje; el tooltip muestra todas las series del mes.
export function Columnas({ datos, series, fmt = fmtM }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 240, I = 46, B = 24, T = 8;
  const max = Math.max(...datos.flatMap((d) => series.map((s) => d[s.key] || 0)), 0);
  if (!datos.length || !max) return <Vacio />;
  const paso = (W - I) / datos.length, ancho = Math.min(22, (paso - 6 - 2 * (series.length - 1)) / series.length);
  const y = (v) => T + (H - T - B) * (1 - v / max);
  return (
    <div className="rp-plot">
      <Leyenda series={series} />
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Grafica por mes" onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((p) => (
          <g key={p}><line x1={I} x2={W} y1={y(max * p)} y2={y(max * p)} className="rp-eje" /><text x={I - 6} y={y(max * p) + 3} textAnchor="end" className="rp-tick">{fmtK(max * p)}</text></g>
        ))}
        {datos.map((d, i) => {
          const x0 = I + i * paso + (paso - (ancho * series.length + 2 * (series.length - 1))) / 2;
          return (
            <g key={d.mes} onMouseEnter={() => setHover(i)}>
              <rect x={I + i * paso} y={0} width={paso} height={H} fill="transparent" />
              {series.map((s, j) => {
                const h = (H - T - B) * ((d[s.key] || 0) / max);
                return <rect key={s.key} x={x0 + j * (ancho + 2)} y={y(d[s.key] || 0)} width={ancho} height={Math.max(h, 0)} rx={Math.min(3, ancho / 2)} fill={s.color} opacity={hover === null || hover === i ? 1 : 0.45} />;
              })}
              <text x={I + i * paso + paso / 2} y={H - 8} textAnchor="middle" className="rp-tick">{nombreMes(d.mes).split(" ")[0]}</text>
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <div className="rp-tip" style={{ left: `${Math.min(78, ((I + hover * paso) / W) * 100)}%` }}>
          <b>{nombreMes(datos[hover].mes)}</b>
          {series.map((s) => <div key={s.key}><i style={{ background: s.color }} />{s.label}: {fmt(datos[hover][s.key])}</div>)}
        </div>
      )}
    </div>
  );
}

export function Linea({ datos, campo, etiqueta, fmt = fmtP, color = COLORES[0] }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 200, I = 46, B = 24, T = 12;
  const vals = datos.map((d) => d[campo] || 0);
  if (!datos.length || !vals.some(Boolean)) return <Vacio />;
  const alto = Math.max(...vals), bajo = Math.min(...vals), holgura = Math.max((alto - bajo) * 0.25, 1);
  const max = alto + holgura, min = Math.max(bajo - holgura, 0);
  const x = (i) => I + ((W - I - 10) * i) / Math.max(datos.length - 1, 1);
  const y = (v) => T + (H - T - B) * (1 - (v - min) / (max - min || 1));
  const ruta = vals.map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`).join(" ");
  return (
    <div className="rp-plot">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={etiqueta} onMouseLeave={() => setHover(null)}>
        {[min, max].map((v) => <g key={v}><line x1={I} x2={W} y1={y(v)} y2={y(v)} className="rp-eje" /><text x={I - 6} y={y(v) + 3} textAnchor="end" className="rp-tick">{fmt(v)}</text></g>)}
        <path d={ruta} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {datos.map((d, i) => (
          <g key={d.mes} onMouseEnter={() => setHover(i)}>
            <rect x={x(i) - 25} y={0} width={50} height={H} fill="transparent" />
            <circle cx={x(i)} cy={y(vals[i])} r={hover === i ? 5 : 3.5} fill={color} stroke="#fff" strokeWidth="2" />
            <text x={x(i)} y={H - 8} textAnchor="middle" className="rp-tick">{nombreMes(d.mes).split(" ")[0]}</text>
          </g>
        ))}
      </svg>
      {hover !== null && <div className="rp-tip" style={{ left: `${Math.min(78, (x(hover) / W) * 100)}%` }}><b>{nombreMes(datos[hover].mes)}</b><div>{etiqueta}: {fmt(vals[hover])}</div></div>}
    </div>
  );
}

// Ranking horizontal: una sola magnitud, un solo color, valor siempre visible
export function Barras({ datos, valor = "venta", fmt = fmtM, extra, color = COLORES[0] }) {
  if (!datos.length) return <Vacio />;
  const max = Math.max(...datos.map((d) => Math.abs(d[valor]) || 0), 1);
  return (
    <div className="rp-barras">
      {datos.map((d) => (
        <div key={d.nombre} className="rp-barra" title={`${d.nombre}: ${fmt(d[valor])}`}>
          <span className="rp-nom">{d.nombre}</span>
          <span className="rp-pista"><i style={{ width: `${(Math.abs(d[valor]) / max) * 100}%`, background: color }} /></span>
          <span className="rp-val">{fmt(d[valor])}{extra ? <small>{extra(d)}</small> : null}</span>
        </div>
      ))}
    </div>
  );
}

// Partes de un todo en una barra segmentada, con leyenda y porcentaje
export function Mezcla({ datos, valor = "venta", fmt = fmtM }) {
  const total = datos.reduce((t, d) => t + (d[valor] || 0), 0);
  if (!total) return <Vacio />;
  return (
    <div className="rp-mezcla">
      <div className="rp-seg">
        {datos.map((d, i) => d[valor] > 0 && <i key={d.nombre} title={`${d.nombre}: ${fmt(d[valor])}`} style={{ flex: d[valor], background: COLORES[i % COLORES.length] }} />)}
      </div>
      <ul>
        {datos.map((d, i) => (
          <li key={d.nombre}><i style={{ background: COLORES[i % COLORES.length] }} /><span>{d.nombre}</span><b>{fmtP((d[valor] / total) * 100)}</b><em>{fmt(d[valor])}</em></li>
        ))}
      </ul>
    </div>
  );
}

export function Tabla({ columnas, filas }) {
  if (!filas.length) return <Vacio />;
  return (
    <div className="table-responsive">
      <table className="table table-sm rp-tabla">
        <thead><tr>{columnas.map((c) => <th key={c.t} className={c.n ? "text-right" : ""}>{c.t}</th>)}</tr></thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>{columnas.map((c) => <td key={c.t} className={c.n ? "text-right" : ""}>{c.r ? c.r(f) : c.f ? c.f(f[c.k]) : f[c.k]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
